/**
 * What a publisher is sent. P5's payload, and only its payload: wave 2 produces
 * this and stops, wave 3 renders it.
 *
 * Proposal section 7.2.1 is the reason this is three fields and not one string.
 * The ruling and the coaching have OPPOSITE properties and must not be blended:
 *
 *   | | ruling | coaching |
 *   | produced by     | code over stored verdicts | may be model-generated |
 *   | wrong how       | a defect, appealable      | unhelpful, dismissible |
 *   | may contradict  | n/a                       | NEVER                  |
 *
 * And section 7.2 adds the third, which is the one the current system cannot
 * produce at all: the FATE. `feed_d EXCLUDED unaligned` and `feed_e EXCLUDED
 * contaminated` are the honest lines. Neither publisher was beaten. One was not
 * comparable and one was not measured, and saying so is different from letting
 * them lose quietly.
 */
import type { Assessment, Fate, Reason } from './observation.js';
import type { WindowId } from './window.js';

/**
 * The ruling. Mechanical, recomputable, appealable, and section 10 protects it.
 *
 * Every entry carries the rubric line it was rejected under, the model that
 * ruled, and a route to a human, because a rejection must be able to be wrong
 * out loud. That is what `Reason` already carries, which is why nothing here
 * flattens one to a string.
 */
export interface RulingRecord {
  /** One entry per gate: mechanical, picture, speech, audience. */
  perGate: Record<string, { passed: boolean; reason: Reason | null }>;
  /** True only when every gate holds. Absence of a verdict is never approval. */
  eligible: boolean;
  /** The derivation that produced this, e.g. `feed_verdict@1`. */
  rule: string;
}

/**
 * The fate. Where this feed landed among the feeds actually offered.
 *
 * `comparedAgainst` is 0 when there was nothing to weigh this feed against, and
 * the renderer must not describe that as a comparison: proposal section 9.2's
 * comparison-vacuity gate is the same fact one layer up.
 */
export interface FateRecord {
  fate: Fate;
  reason: Reason;
  /** How many OTHER feeds cleared every gate in this window. */
  comparedAgainst: number;
  /** True when this feed went to air. */
  selected: boolean;
  /** Score gap to the winner, or null when there was no winner or no score. */
  marginToLeader: number | null;
  /** The rule version the decision named, so a stored fate can be replayed. */
  rule: string;
}

export interface Feedback {
  feedId: string;
  windowId: WindowId;
  ruling: RulingRecord;
  fate: FateRecord;
  /**
   * Apparatus only, and absent whenever the coach has nothing worth saying.
   * Null is a legitimate and common answer: proposal section 11.2 lists "say
   * nothing" as one of the coach's rights.
   */
  coaching: Assessment | null;
  /**
   * This feed's fate on EACH channel it was offered to, §5 of the channels plan.
   *
   * **Absent in event mode, and that is the honest shape**: an event has one
   * board, so `fate` above already says everything there is to say and a
   * one-entry list would be ceremony. In channel mode a feed offered to two
   * channels genuinely has two fates and may be selected on one and refused on
   * the other, which the plan calls a feature: the contrast is what makes the
   * rules legible.
   *
   * The fate record is this project's appeal artifact, so a contribution or a
   * refusal it cannot express is one nobody can appeal. That is why this is a
   * list of per-channel records rather than a winner: being refused on `#music`
   * and selected on `#alaska` in the same window is two rulings, and a publisher
   * owed a reason is owed both.
   */
  channels?: readonly { channelId: string; fate: FateRecord }[];
  /**
   * Channels this publisher ASKED for and was not considered on, §7.
   *
   * **A fate and a refusal to consider are different records, so they are two
   * fields.** `channels` above carries the fates of boards that weighed this
   * feed, and its own rule is that a board which never saw it has no fate for
   * it: inventing one would say a publisher lost somewhere nobody compared
   * them. A membership refusal is the opposite problem. The publisher DID ask,
   * the board DID answer, and the answer was "not you", so silence here is the
   * system knowing something about them that it does not say.
   *
   * Carries the reason rather than a flag, because a place channel's refusal is
   * actionable (pick the location instead of typing it) and a flag is a dead
   * end. A channel the publisher never declared appears in NEITHER list: not
   * asking is not being refused.
   */
  declined?: readonly { channelId: string; reason: string }[];
  at: number;
}

/**
 * What a publisher is told about the company their feed kept this window.
 *
 * Pure, and in `shared/` rather than in the renderer, because this exact
 * sentence has been wrong twice and a string built inside a DOM function is a
 * string no test can reach. Wave 3's working rule 2: expose the logic as a pure
 * function a test can reach, or give it a type the compiler can refuse.
 *
 * The two failures it exists to prevent, both shipped once:
 *
 *   1. A MARGIN BELONGS ONLY TO A FEED THAT WAS COMPARED. "0.00 behind" told an
 *      excluded publisher they were weighed and lost. They were not weighed.
 *   2. VACUITY IS NOT A COMPARISON. With nothing else eligible, saying anything
 *      that reads like a contest invites a publisher to believe an editorial
 *      choice was made about them (proposal 9.2's comparison-vacuity gate, one
 *      layer up).
 */
export function fateContext(fate: FateRecord): string {
  if (fate.comparedAgainst === 0) {
    return fate.selected
      ? 'No other feed cleared every gate in this window, so nothing was weighed against you.'
      : 'No other feed cleared every gate in this window either.';
  }
  const company = `${fate.comparedAgainst} other feed(s) cleared every gate in this window.`;
  // Selected feeds are not behind anyone, and a null margin means the producer
  // declined to compute one. Neither gets a number invented for it here.
  return fate.marginToLeader !== null && !fate.selected
    ? `${company} You were ${fate.marginToLeader.toFixed(2)} behind the feed that went to air.`
    : company;
}

/**
 * What this publisher is owed about each CHANNEL, §5 and §7.
 *
 * Pure and in `shared/` for the reason `fateContext` is: a sentence built
 * inside a DOM function is a sentence no test can reach, and this file's own
 * record is that the fate line has been wrong twice.
 *
 * **Two lists, two claims, and they are not interchangeable.** `channels` holds
 * boards that WEIGHED this feed, so each line is a fate. `declined` holds
 * boards this publisher ASKED for and was not considered on, so each line is a
 * membership refusal and carries what to do about it. A refusal rendered as a
 * fate would tell somebody they lost a comparison nobody ran, which is failure
 * 1 in `fateContext` above arriving one level out.
 *
 * Empty in event mode, where one board decided and the fate line above is the
 * whole answer. An empty list renders nothing rather than a heading with
 * nothing under it.
 */
export function channelLines(f: Feedback): readonly { channelId: string; line: string; weighed: boolean }[] {
  return [
    ...(f.channels ?? []).map((c) => ({
      channelId: c.channelId,
      line: fateContext(c.fate),
      weighed: true,
    })),
    ...(f.declined ?? []).map((d) => ({
      channelId: d.channelId,
      // The board's own words. Rewriting a refusal here would be a second copy
      // of a decision, and the reason is the actionable half.
      line: d.reason,
      weighed: false,
    })),
  ];
}

/**
 * The three artifacts must stay separable, so this asserts they did not merge.
 *
 * Wave 3 inherits "ruling, fate and coaching arrive separately and must stay
 * visually separable", and a renderer cannot keep them apart if the producer
 * already blended them. A coach note that has been copied into a rubric line is
 * a content finding written by a coach, which section 11.3 refuses, so it is
 * checked here rather than trusted.
 */
export function separable(f: Feedback): boolean {
  const note = f.coaching?.note;
  if (note === undefined || note.length === 0) return true;
  const rulingText = [
    ...Object.values(f.ruling.perGate).map((g) => g.reason?.rubricLine ?? ''),
    f.fate.reason.rubricLine,
  ];
  return !rulingText.some((line) => line.includes(note));
}
